<template>
  <div class="action-view-container">
    <div
      v-if="!store.viewData.loaded"
      class="Container df ac"
      style="height: 400px; color: var(--copia-teal-02)"
    >
      <div class="ui active centered inline loader" />
    </div>
    <!-- body uses v-show, not v-if: the child views own the data polling, so
         they must mount immediately (also, v-show can't hide the loader above:
         the .df utility is display:flex !important) -->
    <div
      v-show="store.viewData.loaded"
      class="action-view-body"
      :class="{ 'summary-mode': isSummary }"
    >
      <div class="action-view-left">
        <div class="action-view-title">Actions</div>
        <div class="action-info-summary">
          {{ run.title }}
          <button
            class="run_cancel poping up"
            @click="cancelRun()"
            v-if="run.canCancel"
            data-content="Cancel workflow"
            data-variation="inverted tiny"
          >
            <i class="stop circle outline icon" />
          </button>
        </div>
        <div class="job-group-section">
          <div class="job-brief-list">
            <a
              class="job-brief-item summary-brief-item"
              :class="{ active: isSummary }"
              :href="run.htmlurl"
            >
              <SvgIcon name="octicon-list-unordered" class="mr-2" />
              Summary
            </a>
            <div class="job-list-header">All jobs</div>
            <a
              class="job-brief-item"
              :class="{ active: !isSummary && Number(jobIndex) === index }"
              v-for="(job, index) in run.jobs"
              :key="job.id"
              :href="run.htmlurl + '/jobs/' + index"
            >
              <span class="job-brief-item-left">
                <ActionRunStatusIcon :status="job.status" class="mr-2" />
                <span class="gt-ellipsis">{{ job.name }}</span>
                <i
                  v-if="job.status === 'running'"
                  class="notched spinner loading icon"
                />
              </span>
              <span class="job-brief-item-right">
                <span class="job-brief-duration">{{ job.duration }}</span>
                <button
                  class="job-brief-rerun poping up"
                  @click.prevent="rerunJob(index)"
                  v-if="job.canRerun"
                  data-content="Re-run job"
                  data-variation="inverted tiny"
                >
                  <SvgIcon name="octicon-sync" class="ui text black" />
                </button>
              </span>
            </a>
          </div>
        </div>
        <div class="job-artifacts" v-if="artifacts.length">
          <div class="job-artifacts-title">Artifacts</div>
          <ul class="job-artifacts-list">
            <li
              class="job-artifacts-item"
              v-for="artifact in artifacts"
              :key="artifact.name"
            >
              <template v-if="artifact.status !== 'expired'">
                <a
                  class="job-artifacts-link"
                  target="_blank"
                  :href="getArtifactDownloadURL(artifact.name)"
                >
                  <SvgIcon name="octicon-file" class="mr-2" />
                  <span class="gt-ellipsis">{{ artifact.name }}</span>
                </a>
                <button
                  v-if="run.canDeleteArtifact"
                  class="job-artifacts-delete poping up"
                  @click="deleteArtifact(artifact.name)"
                  data-content="Delete artifact"
                  data-variation="inverted tiny"
                >
                  <SvgIcon name="octicon-trashcan" />
                </button>
              </template>
              <span v-else class="job-artifacts-expired">
                <SvgIcon name="octicon-file" class="mr-2" />
                <span class="gt-ellipsis">{{ artifact.name }}</span>
                <span class="artifact-expired-label">Expired</span>
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div class="action-view-right">
        <div
          class="action-view-title"
          :class="{ 'run-summary-title': isSummary }"
        >
          {{ isSummary ? "Run Summary" : "Status" }}
        </div>
        <ActionRunSummaryView v-if="isSummary" :store="store" />
        <ActionRunJobView
          v-else
          :store="store"
          :run-index="runIndex"
          :job-index="jobIndex"
          :actions-url="actionsURL"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, watch } from "vue";
import { SvgIcon } from "../svg.js";
import ActionRunJobView from "./ActionRunJobView.vue";
import ActionRunStatusIcon from "./ActionRunStatusIcon.vue";
import ActionRunSummaryView from "./ActionRunSummaryView.vue";
import { actionsFetch, createActionRunViewStore } from "./ActionRunView.js";

const { csrf: csrfToken } = window.config;

// Props
const props = defineProps({
  runIndex: {
    type: String,
    default: "",
  },
  jobIndex: {
    type: String,
    default: "",
  },
  actionsURL: {
    type: String,
    default: "",
  },
});

// No job in the path (job index empty or -1) => run-level Summary view.
const isSummary = computed(
  () => props.jobIndex === "" || Number(props.jobIndex) < 0,
);

// Shared state: the Summary view polls the run endpoint through the store,
// the job view polls the job endpoint and writes run data back into it.
const store = createActionRunViewStore(props.actionsURL, props.runIndex);
const run = computed(() => store.viewData.currentRun);
const artifacts = computed(() => store.viewData.runArtifacts);

// Methods
const getArtifactDownloadURL = (name) =>
  `${run.value.htmlurl}/artifacts/${encodeURIComponent(name)}`;

const deleteArtifact = async (name) => {
  if (!window.confirm(`Delete artifact "${name}"?`)) return;
  try {
    await window.fetch(getArtifactDownloadURL(name), {
      method: "DELETE",
      headers: {
        "X-Csrf-Token": csrfToken,
      },
    });
    await store.forceReloadCurrentRun();
  } catch (error) {
    console.error("Error deleting artifact:", error);
  }
};

// rerun a job, then bring polling back to life so the new state shows without
// a manual refresh (the job view resumes its own poll via its run.done watcher)
const rerunJob = async (idx) => {
  try {
    await actionsFetch(`${run.value.htmlurl}/jobs/${idx}/rerun`);
    if (isSummary.value) {
      await store.resumePollingCurrentRun();
    } else {
      await store.forceReloadCurrentRun();
    }
  } catch (error) {
    console.error("Error rerunning job:", error);
  }
};

// cancel a run
const cancelRun = () => {
  actionsFetch(`${run.value.htmlurl}/cancel`).catch((error) => {
    console.error("Error canceling run:", error);
  });
};

// Only reinitialize popups when popup-bearing elements change
const popupStateKey = computed(() =>
  JSON.stringify({
    canCancel: run.value.canCancel,
    canDeleteArtifact: run.value.canDeleteArtifact,
    jobs: run.value.jobs.map((j) => ({ id: j.id, canRerun: j.canRerun })),
    artifacts: artifacts.value.map((a) => ({
      name: a.name,
      status: a.status,
    })),
  }),
);

watch(popupStateKey, async () => {
  await nextTick();
  window.$(".action-view-container .poping.up").popup();
});
</script>

<style scoped lang="less">
// some elements are not managed by vue, so we need to use _actions.less in addition.

.action-view-body {
  display: flex;
  height: calc(
    100vh - 266px
  ); // fine tune this value to make the main view has full height

  // Summary mode scrolls the whole page instead of an inner box: summaries are
  // arbitrary-length content, so the body grows with it (the job view keeps the
  // fixed height for its log-scroll layout).
  &.summary-mode {
    height: auto;
  }
}

.action-view-title {
  background-color: var(--copia-grey-01);
  color: var(--copia-grey-04);
  border-radius: 2px;
  padding: 6px;
  padding-left: 10px;
  border-bottom: solid;
  border-color: var(--copia-grey-02);
  border-width: thin;
  margin-bottom: 6px;
}

// ================
// action view summary

.action-info-summary {
  display: flex;
  align-items: center;
  font-size: 125%;
  font-weight: bold;
  padding: 10px;
  button.run_cancel {
    flex-shrink: 0;
    border: none;
    color: var(--color-red);
    background-color: transparent;
    outline: none;
    cursor: pointer;
    transition: transform 0.2s;
  }
  button.run_cancel:hover {
    transform: scale(130%);
  }
}

// ================
// action view left

.action-view-left {
  background-color: var(--color-console-bg);
  border: solid;
  border-color: var(--copia-grey-02);
  border-radius: 8px;
  border-width: thin;
  width: 30%;
  max-width: 350px;
  overflow-y: auto;
  margin: 0px 10px;
  height: 80%;
}

.job-group-section {
  padding: 0 10px;

  .job-group-summary {
    margin: 5px 0;
    padding: 10px;
  }

  .job-list-header {
    margin: 10px 0 2px;
    font-weight: 600;
  }

  .job-brief-list {
    a.job-brief-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;
      margin: 5px 0;
      padding: 10px;
      color: var(--copia-black);
      border-radius: 5px;
      text-decoration: none;

      .job-brief-item-left {
        display: inline-flex;
        align-items: center;
        min-width: 0;
      }

      .job-brief-item-right {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;
      }

      .job-brief-duration {
        font-size: 12px;
        color: var(--copia-grey-03);
      }

      button.job-brief-rerun {
        border: none;
        background-color: transparent;
        outline: none;
        cursor: pointer;
        transition: transform 0.2s;
      }
      button.job-brief-rerun:hover {
        transform: scale(130%);
      }
    }
    a.job-brief-item:hover {
      background-color: var(--copia-grey-01);
    }
    a.summary-brief-item {
      justify-content: flex-start;
      color: var(--copia-grey-04);

      svg {
        color: var(--copia-teal-02);
      }
    }
    // Selected nav row (Summary or a job) is a solid teal bar per the Figma
    // handoff; !important is needed because ActionRunStatusIcon and the rerun
    // button set their colors inline / via semantic-ui classes.
    a.job-brief-item.active,
    a.job-brief-item.active:hover {
      background-color: var(--copia-teal-02);
      color: var(--copia-white);
      font-weight: 500;

      svg {
        color: var(--copia-white) !important;
      }

      .job-brief-duration {
        color: var(--copia-white);
        opacity: 0.85;
      }
    }
  }
}

.job-artifacts {
  margin: 10px;
  padding: 10px 0 0;
  border-top: 1px solid var(--copia-grey-02);
}

.job-artifacts-title {
  font-weight: 600;
  margin-bottom: 6px;
}

.job-artifacts-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.job-artifacts-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 6px 0;
}

.job-artifacts-link {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  color: var(--copia-grey-04);
  &:hover {
    color: var(--copia-teal-02);
  }
}

.job-artifacts-delete {
  border: none;
  background: transparent;
  color: var(--copia-red-01);
  cursor: pointer;
}

.job-artifacts-expired {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--copia-grey-03);
}

.artifact-expired-label {
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 10px;
  background: var(--copia-grey-02);
  color: var(--copia-grey-04);
}

// ================
// action view right

.action-view-right {
  border: solid;
  border-radius: 8px;
  border-width: thin;
  border-color: var(--copia-grey-02);
  flex: 1;
  // flex items default to min-width auto: a summary with a long unbreakable
  // line would otherwise widen this panel past the viewport and crush the
  // sidebar instead of wrapping.
  min-width: 0;
  background-color: var(--color-console-bg);
  color: var(--color-console-fg);
  max-height: 100%;
  margin-right: 10px;

  display: flex;
  flex-direction: column;

  // In summary mode the run-meta line extends the grey title band (Figma),
  // so drop the gap the title normally leaves before the content.
  .action-view-title.run-summary-title {
    margin-bottom: 0;
  }
}
</style>
